// src/public/PublicSalesAssessmentQuestions.js
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  CheckCircle as CompleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import useAntiCheat from '../hooks/useAntiCheat';
import salesAssessmentQuestions from './utils/sales-assessment-questions.json';

// Sin timeout, axios espera indefinidamente y un backend colgado deja el modal
// de "enviando" girando sin fin. Por llamada, no en la instancia, para no
// afectar las rutas lentas de la app (exportes, generacion de documentos).
const SAVE_TIMEOUT_MS = 15000;
const COMPLETE_TIMEOUT_MS = 45000;
const COMPLETE_ATTEMPTS = 2;

const PublicSalesAssessmentQuestions = ({ testId, testData, timeRemaining: initialTime, onComplete }) => {
  const { t } = useTranslation();
  const [responses, setResponses] = useState(testData?.responses || {});
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCompletingModal, setShowCompletingModal] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(initialTime);

  // Momento exacto en que vence el test, anclado al reloj. El conteo NO
  // resta 1 por tick: los navegadores estrangulan los timers de pestanas
  // en segundo plano (hasta un tick por minuto), y restando por tick el
  // cronometro se pausaba al cambiar de pestana, estirando el tiempo real
  // del candidato. Calculando contra el reloj, al volver (o en el propio
  // tick estrangulado) el conteo se corrige solo y dispara el cierre si
  // ya vencio.
  const deadlineRef = useRef(Date.now() + (initialTime || 0) * 1000);

  // Refs para tracking de tiempo
  const questionStartTime = useRef(Date.now());
  const questionTimes = useRef({});
  const timerRef = useRef(null);

  // Ref que siempre apunta a la versión más reciente de handleAutoComplete.
  // El setInterval del timer se crea una sola vez al montar, por lo que su
  // closure queda congelado en el primer render: llamar handleAutoComplete
  // directamente enviaba el estado inicial (responses vacío) al completar
  // por tiempo. Ver useEffect que actualiza este ref más abajo.
  const handleAutoCompleteRef = useRef(null);

  // Evita completar el test dos veces (timer y botón compitiendo)
  const completingRef = useRef(false);

  // Inicializar hook anti-trampa
  const { getEventsSummary, resetEvents } = useAntiCheat({
    enabled: true,
    userId: testData.applicantEmail,
    testId: testId
  });

  // Ref para trackear qué preguntas confirmó el backend
  const savedQuestionsRef = useRef(new Set());

  // Preguntas respondidas cuyo guardado falló: viajan de nuevo con el siguiente
  // guardado. Antes se marcaban como guardadas igual y no se reintentaban nunca.
  const pendingQuestionsRef = useRef(new Map());

  // Preguntas para las que YA se envió una vez el resumen anti-trampa. En un
  // reintento se manda `antiCheatEvents: null`, porque el backend acumula esos
  // eventos con push sin deduplicar y `completeTest` recalcula el índice de
  // confiabilidad sobre ese arreglo al cerrar: reenviarlos movería el índice.
  // No se puede saber desde el cliente si el primer intento alcanzó a escribir.
  const attemptedQuestionsRef = useRef(new Set());

  const totalQuestions = salesAssessmentQuestions.length;
  const [currentQuestion, setCurrentQuestion] = useState(
    Math.max(0, Math.min((testData?.currentQuestion || 1) - 1, totalQuestions - 1))
  );
  const currentQuestionData = salesAssessmentQuestions[currentQuestion];

  // Timer countdown (anclado al reloj, no al numero de ticks)
  useEffect(() => {
    timerRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.round((deadlineRef.current - Date.now()) / 1000));
      setTimeRemaining(remaining);

      if (remaining <= 0) {
        clearInterval(timerRef.current);
        // Usar el ref para invocar la version vigente del handler
        handleAutoCompleteRef.current?.();
      }
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Cargar respuesta guardada al cambiar de pregunta
  useEffect(() => {
    const savedAnswer = responses[currentQuestion + 1];
    if (savedAnswer) {
      setSelectedAnswer(savedAnswer);
    } else {
      setSelectedAnswer(null);
    }
    setError('');

    // Reiniciar tiempo de pregunta
    questionStartTime.current = Date.now();
  }, [currentQuestion, responses]);

  const handleAnswerChange = (event) => {
    setSelectedAnswer(event.target.value);
    setError('');
  };

  // Calcular tiempo gastado en pregunta actual
  const getQuestionTimeSpent = () => {
    const now = Date.now();
    const timeSpentMs = now - questionStartTime.current;
    return Math.round(timeSpentMs / 1000); // en segundos
  };

  const saveToBackend = async (questionId, answer, timeSpent, antiCheatEvents = null) => {
    try {
      await api.put(
        `/sales-assessments-public/${testId}/response`,
        {
          applicantEmail: testData.applicantEmail,
          questionId,
          answer,
          timeSpent,
          antiCheatEvents
        },
        { timeout: SAVE_TIMEOUT_MS }
      );
      return true;
    } catch (err) {
      console.error('Error saving response:', err);
      return false;
    }
  };

  /**
   * Reenvía las preguntas cuyo guardado quedó pendiente.
   *
   * Van SIN eventos anti-trampa: es un reintento y no se puede saber si el
   * primer envío alcanzó a escribir en Firestore. La respuesta y el tiempo se
   * escriben con dot notation, o sea sobrescritura idempotente, así que
   * reenviarlos es inofensivo.
   */
  const flushPendingQuestions = async () => {
    if (pendingQuestionsRef.current.size === 0) return;

    for (const [questionId, pending] of Array.from(pendingQuestionsRef.current.entries())) {
      const ok = await saveToBackend(questionId, pending.answer, pending.timeSpent, null);
      if (ok) {
        pendingQuestionsRef.current.delete(questionId);
        savedQuestionsRef.current.add(questionId);
      }
    }
  };

  /**
   * Guarda la pregunta actual si el backend aún no la confirmó, arrastrando
   * primero las que quedaron pendientes.
   */
  const persistQuestion = async (questionId, answer, timeSpent) => {
    await flushPendingQuestions();

    if (savedQuestionsRef.current.has(questionId)) return true;

    const firstAttempt = !attemptedQuestionsRef.current.has(questionId);
    const antiCheatEvents = firstAttempt ? getEventsSummary() : null;
    attemptedQuestionsRef.current.add(questionId);

    const ok = await saveToBackend(questionId, answer, timeSpent, antiCheatEvents);

    if (ok) {
      savedQuestionsRef.current.add(questionId);
      pendingQuestionsRef.current.delete(questionId);
    } else {
      pendingQuestionsRef.current.set(questionId, { answer, timeSpent });
    }

    return ok;
  };

  /**
   * Cierra el test en el backend. Reintenta, porque es el último punto donde se
   * puede salvar todo lo respondido.
   *
   * Si el primer intento llegó al servidor pero se perdió la respuesta, el
   * reintento devuelve 400 diciendo que el test ya no está en "started": eso es
   * éxito, no error.
   */
  const submitTest = async (allResponses, totalTimeSpent) => {
    for (let attempt = 0; attempt < COMPLETE_ATTEMPTS; attempt += 1) {
      try {
        const response = await api.put(
          `/sales-assessments-public/${testId}/complete`,
          {
            applicantEmail: testData.applicantEmail,
            responses: allResponses,
            totalTimeSpent: totalTimeSpent
          },
          { timeout: COMPLETE_TIMEOUT_MS }
        );

        if (response.data.errorCode === 0) return true;
      } catch (err) {
        const currentStatus = err.response?.data?.response?.currentStatus;
        if (err.response?.status === 400 && currentStatus && currentStatus !== 'started') {
          return true;
        }
        console.error('Error completing test:', err);
      }
    }

    return false;
  };

  /**
   * Guarda la posición del candidato.
   *
   * Recibe el índice (base 0) y guarda el número de pregunta (base 1), que es
   * como lo lee este componente al retomar —`(currentQuestion || 1) - 1`— y como
   * lo escribe `saveResponse` en el backend. Antes se mandaba el índice crudo a
   * un campo que se lee base 1, así que al retomar quedaba corrido en uno.
   */
  const updateCurrentQuestionInBackend = async (questionIndex) => {
    try {
      await api.put(
        `/sales-assessments-public/${testId}/current-question`,
        {
          applicantEmail: testData.applicantEmail,
          currentQuestion: questionIndex + 1
        },
        { timeout: SAVE_TIMEOUT_MS }
      );
    } catch (err) {
      console.error('Error updating current question:', err);
    }
  };

  const saveCurrentResponse = () => {
    if (selectedAnswer === null) {
      setError(t('salesAssessment.public.selectAnswer'));
      return false;
    }

    setResponses(prev => ({
      ...prev,
      [currentQuestion + 1]: selectedAnswer
    }));

    return true;
  };

  const handleNext = async () => {
    if (!saveCurrentResponse()) return;

    // Calcular tiempo gastado
    const timeSpent = getQuestionTimeSpent();
    questionTimes.current[currentQuestion + 1] = timeSpent;

    // Guardar respuesta con tiempo y eventos anticheat (solo si no se confirmó antes)
    const savedOk = await persistQuestion(currentQuestion + 1, selectedAnswer, timeSpent);
    if (savedOk) {
      resetEvents(); // Limpiar eventos para la siguiente pregunta
    }

    if (currentQuestion < totalQuestions - 1) {
      // La posición la guarda el useEffect de `currentQuestion`; llamarlo también
      // acá duplicaba la request con el mismo valor.
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handleBack = async () => {
    if (!saveCurrentResponse()) return;

    // Calcular tiempo gastado
    const timeSpent = getQuestionTimeSpent();
    questionTimes.current[currentQuestion + 1] = timeSpent;

    // Guardar respuesta con tiempo y eventos anticheat (solo si no se confirmó antes)
    const savedOk = await persistQuestion(currentQuestion + 1, selectedAnswer, timeSpent);
    if (savedOk) {
      resetEvents(); // Limpiar eventos para la siguiente pregunta
    }

    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    if (completingRef.current) return;
    if (!saveCurrentResponse()) return;

    completingRef.current = true;
    setSaving(true);
    setShowCompletingModal(true);

    // Detener timer
    if (timerRef.current) clearInterval(timerRef.current);

    try {
      // Calcular tiempo de la última pregunta
      const timeSpent = getQuestionTimeSpent();
      questionTimes.current[currentQuestion + 1] = timeSpent;

      // Guardar la respuesta actual (y arrastrar las pendientes)
      await persistQuestion(currentQuestion + 1, selectedAnswer, timeSpent);

      // Construir objeto completo de respuestas
      const allResponses = {
        ...responses,
        [currentQuestion + 1]: selectedAnswer
      };

      // Calcular tiempo total
      const totalTimeSpent = Object.values(questionTimes.current).reduce((sum, time) => sum + time, 0);

      if (await submitTest(allResponses, totalTimeSpent)) {
        onComplete({ completed: true });
      } else {
        // Cierre manual: se queda en las preguntas para poder reintentar
        completingRef.current = false;
        setError(t('salesAssessment.public.completeTestError'));
        setShowCompletingModal(false);
      }
    } catch (err) {
      console.error('Error completing test:', err);
      completingRef.current = false;
      setError(t('salesAssessment.public.completeTestError'));
      setShowCompletingModal(false);
    } finally {
      setSaving(false);
    }
  };

  const handleAutoComplete = async () => {
    if (completingRef.current) return;
    completingRef.current = true;

    if (timerRef.current) clearInterval(timerRef.current);

    setSaving(true);
    setShowCompletingModal(true);

    let submitted = false;

    try {
      // Guardar tiempo de la pregunta actual
      const timeSpent = getQuestionTimeSpent();
      questionTimes.current[currentQuestion + 1] = timeSpent;

      // Si hay respuesta seleccionada, guardarla (y arrastrar las pendientes)
      if (selectedAnswer !== null) {
        await persistQuestion(currentQuestion + 1, selectedAnswer, timeSpent);
      } else {
        await flushPendingQuestions();
      }

      // Construir respuestas completas
      const allResponses = selectedAnswer !== null
        ? { ...responses, [currentQuestion + 1]: selectedAnswer }
        : responses;

      // Calcular tiempo total
      const totalTimeSpent = Object.values(questionTimes.current).reduce((sum, time) => sum + time, 0);

      // Completar el test (aunque no estén todas las respuestas)
      submitted = await submitTest(allResponses, totalTimeSpent);
    } catch (err) {
      console.error('Error auto-completing test:', err);
    } finally {
      setSaving(false);
      // Su tiempo terminó: no puede seguir respondiendo, así que sale de las
      // preguntas pase lo que pase. Si el envío falló se le dice, en vez de
      // dejarlo atrapado con el reloj en 0 y un error que no puede resolver.
      onComplete({ completed: true, timeExpired: true, submitFailed: !submitted });
    }
  };

  // Mantener el ref del timer apuntando a la versión más reciente de
  // handleAutoComplete (con responses/selectedAnswer actualizados)
  useEffect(() => {
    handleAutoCompleteRef.current = handleAutoComplete;
  });

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Actualizar pregunta actual en el backend al cambiar
  useEffect(() => {
    if (currentQuestion > 0) {
      updateCurrentQuestionInBackend(currentQuestion);
    }
  }, [currentQuestion]);

  const isLastQuestion = currentQuestion === totalQuestions - 1;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Título y Timer */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          {t('salesAssessment.public.testTitle')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            mt: 2
          }}
        >
          <Typography
            variant="h6"
            color={timeRemaining < 300 ? 'error' : 'primary'}
          >
            {t('salesAssessment.public.timeRemaining')}: {formatTime(timeRemaining)}
          </Typography>
        </Box>
      </Box>

      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Progreso */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('salesAssessment.public.questionProgress', {
                current: currentQuestion + 1,
                total: totalQuestions
              })}
            </Typography>
            <Typography variant="body2" color="primary" fontWeight="bold">
              {Math.round(progress)}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
        </Box>

        {/* Enunciado de la pregunta */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom color="primary">
            {currentQuestionData.statement}
          </Typography>
        </Box>

        {/* Opciones de respuesta */}
        <RadioGroup value={selectedAnswer || ''} onChange={handleAnswerChange}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {currentQuestionData.options.map((option) => (
              <Paper
                key={option.option}
                elevation={selectedAnswer === option.option ? 3 : 1}
                sx={{
                  p: 2,
                  border: 2,
                  borderColor: selectedAnswer === option.option ? 'primary.main' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.light',
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={() => setSelectedAnswer(option.option)}
              >
                <FormControlLabel
                  value={option.option}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t('salesAssessment.public.optionLabel', {
                          option: option.option.toUpperCase()
                        })}
                      </Typography>
                      <Typography variant="body1">
                        {option.text}
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </Paper>
            ))}
          </Box>
        </RadioGroup>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Botones de navegación */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={handleBack}
            disabled={currentQuestion === 0 || saving}
          >
            {t('salesAssessment.public.previous')}
          </Button>

          {isLastQuestion ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<CompleteIcon />}
              onClick={handleComplete}
              disabled={saving || selectedAnswer === null}
            >
              {saving ? t('salesAssessment.public.completing') : t('salesAssessment.public.completeTest')}
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={handleNext}
              disabled={selectedAnswer === null || saving}
            >
              {t('salesAssessment.public.next')}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Modal de completando test */}
      <Dialog
        open={showCompletingModal}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(5px)',
          }
        }}
        slotProps={{
          backdrop: {
            style: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            }
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            {t('salesAssessment.public.completingEvaluation')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('salesAssessment.public.processingResponses')}
            <br />
            {t('salesAssessment.public.doNotCloseWindow')}
          </Typography>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default PublicSalesAssessmentQuestions;